import React, { useState } from 'react';
import { Search, ShoppingBag, Video, TrendingUp, Globe, Plus, X } from 'lucide-react';
import { GoogleAdsContent } from '../types/ads.types';
import { PLATFORM_REQUIREMENTS, AD_PLATFORMS } from '../config/adPlatformConfig';
import MediaUploader from '../components/MediaUploader';
import VariationToggle from '../components/VariationToggle';
import PlatformEditorTabs from '../components/PlatformEditorTabs';
import AssetSubTabs from '../components/AssetSubTabs';

interface BlobUrlManager {
  add: (url: string) => void;
  remove: (url: string) => void;
  cleanup: () => void;
}

interface GoogleAdsEditorProps {
  content: GoogleAdsContent;
  isEditing: boolean;
  onChange: (field: keyof GoogleAdsContent, value: any) => void;
  blobManager?: BlobUrlManager;
  contentId?: number;
  apiBaseUrl?: string;
}

export default function GoogleAdsEditor({ content, isEditing, onChange, blobManager, contentId, apiBaseUrl }: GoogleAdsEditorProps) {
  const requirements = PLATFORM_REQUIREMENTS.google;
  const platform = AD_PLATFORMS.google;
  const [currentHeadlineIndex, setCurrentHeadlineIndex] = useState(0);
  const [currentDescriptionIndex, setCurrentDescriptionIndex] = useState(0);
  const [showAllHeadlines, setShowAllHeadlines] = useState(false);
  const [showAllDescriptions, setShowAllDescriptions] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [activeAssetSubTab, setActiveAssetSubTab] = useState('text');
  const [selectedCampaignTypes, setSelectedCampaignTypes] = useState<string[]>(
    content.campaignTypes || ['search']
  );

  const tabs = [
    { id: 'settings', label: 'Settings' },
    { id: 'assets', label: 'Assets' },
    { id: 'targeting', label: 'Targeting' },
    { id: 'budget', label: 'Budget' }
  ];

  // Dynamic asset tabs based on campaign types
  const getAssetSubTabs = () => {
    const tabs = [];
    if (selectedCampaignTypes.includes('search') || selectedCampaignTypes.includes('shopping')) {
      tabs.push({ id: 'text', label: 'Text' });
      tabs.push({ id: 'keywords', label: 'Keywords' });
    }
    if (selectedCampaignTypes.includes('display') || selectedCampaignTypes.includes('discovery')) {
      tabs.push({ id: 'images', label: 'Images' });
    }
    if (selectedCampaignTypes.includes('video') || selectedCampaignTypes.includes('performance_max')) {
      tabs.push({ id: 'videos', label: 'Videos' });
    }
    // Always show text if no specific types
    if (tabs.length === 0) {
      tabs.push({ id: 'text', label: 'Text' });
    }
    return tabs;
  };

  const assetSubTabs = getAssetSubTabs();

  const handleCampaignTypeToggle = (type: string) => {
    const newTypes = selectedCampaignTypes.includes(type)
      ? selectedCampaignTypes.filter(t => t !== type)
      : [...selectedCampaignTypes, type];
    setSelectedCampaignTypes(newTypes);
    onChange('campaignTypes', newTypes);
  };

  // Initialize variations if they don't exist
  const headlineVariations = content.headlines || [''];
  const descriptionVariations = content.descriptions || [''];


  return (
    <div className="space-y-6">
      <PlatformEditorTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'settings' && (
        <>
          {/* Campaign Info */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Search className="w-5 h-5 mr-2 text-blue-600" />
          Campaign Settings
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campaign Name *
            </label>
            <input
              type="text"
              value={content.campaignName || ''}
              onChange={(e) => onChange('campaignName', e.target.value)}
              disabled={!isEditing}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="Enter campaign name..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Campaign Types (select all that apply)
            </label>
            <div className="grid grid-cols-3 gap-3">
              {platform.formats.map((format) => {
                const value = format.toLowerCase().replace(' ', '_');
                return (
                  <label key={value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCampaignTypes.includes(value)}
                      onChange={() => handleCampaignTypeToggle(value)}
                      disabled={!isEditing}
                      className="rounded text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm">{format}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </div>
        </>
      )}

      {activeTab === 'assets' && (
        <>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <AssetSubTabs
              tabs={assetSubTabs}
              activeTab={activeAssetSubTab}
              onTabChange={setActiveAssetSubTab}
            />

            {activeAssetSubTab === 'text' && (
              <div className="space-y-6">

        {/* Headlines */}
        <div className="mb-6">
          <VariationToggle
            variations={headlineVariations}
            label={`Headlines (${headlineVariations.length}/${requirements.headlines.max})`}
            currentIndex={currentHeadlineIndex}
            onIndexChange={setCurrentHeadlineIndex}
            isEditing={isEditing}
            maxCharLimit={requirements.headlines.charLimit}
            showAllVariations={showAllHeadlines}
            onToggleShowAll={() => setShowAllHeadlines(!showAllHeadlines)}
          />
          {isEditing && (
            <div className="mt-2">
              <input
                type="text"
                value={headlineVariations[currentHeadlineIndex] || ''}
                onChange={(e) => {
                  const newVariations = [...headlineVariations];
                  newVariations[currentHeadlineIndex] = e.target.value;
                  onChange('headlines', newVariations);
                }}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                maxLength={requirements.headlines.charLimit}
                placeholder={`Headline ${currentHeadlineIndex + 1}`}
              />
              <div className="flex justify-between mt-2">
                <button
                  onClick={() => {
                    if (headlineVariations.length < requirements.headlines.max) {
                      const newVariations = [...headlineVariations, ''];
                      onChange('headlines', newVariations);
                      setCurrentHeadlineIndex(newVariations.length - 1);
                    }
                  }}
                  disabled={headlineVariations.length >= requirements.headlines.max}
                  className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                >
                  + Add Headline
                </button>
                {headlineVariations.length > 1 && (
                  <button
                    onClick={() => {
                      const newVariations = headlineVariations.filter((_, i) => i !== currentHeadlineIndex);
                      onChange('headlines', newVariations);
                      setCurrentHeadlineIndex(Math.max(0, currentHeadlineIndex - 1));
                    }}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Remove Current
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Descriptions */}
        <div className="mb-6">
          <VariationToggle
            variations={descriptionVariations}
            label={`Descriptions (${descriptionVariations.length}/${requirements.descriptions.max})`}
            currentIndex={currentDescriptionIndex}
            onIndexChange={setCurrentDescriptionIndex}
            isEditing={isEditing}
            maxCharLimit={requirements.descriptions.charLimit}
            showAllVariations={showAllDescriptions}
            onToggleShowAll={() => setShowAllDescriptions(!showAllDescriptions)}
          />
          {isEditing && (
            <div className="mt-2">
              <textarea
                value={descriptionVariations[currentDescriptionIndex] || ''}
                onChange={(e) => {
                  const newVariations = [...descriptionVariations];
                  newVariations[currentDescriptionIndex] = e.target.value;
                  onChange('descriptions', newVariations);
                }}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-16"
                maxLength={requirements.descriptions.charLimit}
                placeholder={`Description ${currentDescriptionIndex + 1}`}
              />
              <div className="flex justify-between mt-2">
                <button
                  onClick={() => {
                    if (descriptionVariations.length < requirements.descriptions.max) {
                      const newVariations = [...descriptionVariations, ''];
                      onChange('descriptions', newVariations);
                      setCurrentDescriptionIndex(newVariations.length - 1);
                    }
                  }}
                  disabled={descriptionVariations.length >= requirements.descriptions.max}
                  className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                >
                  + Add Description
                </button>
                {descriptionVariations.length > 1 && (
                  <button
                    onClick={() => {
                      const newVariations = descriptionVariations.filter((_, i) => i !== currentDescriptionIndex);
                      onChange('descriptions', newVariations);
                      setCurrentDescriptionIndex(Math.max(0, currentDescriptionIndex - 1));
                    }}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Remove Current
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* URLs */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Final URL *
            </label>
            <input
              type="url"
              value={content.finalUrl || ''}
              onChange={(e) => onChange('finalUrl', e.target.value)}
              disabled={!isEditing}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="https://example.com/landing-page"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display URL
            </label>
            <input
              type="text"
              value={content.displayUrl || ''}
              onChange={(e) => onChange('displayUrl', e.target.value)}
              disabled={!isEditing}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="example.com"
            />
          </div>
        </div>

        {/* URL Paths */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Path 1 (optional)
            </label>
            <input
              type="text"
              value={content.path1 || ''}
              onChange={(e) => onChange('path1', e.target.value)}
              disabled={!isEditing}
              maxLength={requirements.path.charLimit}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="category"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Path 2 (optional)
            </label>
            <input
              type="text"
              value={content.path2 || ''}
              onChange={(e) => onChange('path2', e.target.value)}
              disabled={!isEditing}
              maxLength={requirements.path.charLimit}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="product"
            />
          </div>
        </div>
      </div>
            )}

            {activeAssetSubTab === 'keywords' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Keywords
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(content.keywords || []).map((keyword, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {keyword}
                        {isEditing && (
                          <button
                            onClick={() => {
                              const newKeywords = content.keywords?.filter((_, i) => i !== index);
                              onChange('keywords', newKeywords);
                            }}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                  {isEditing && (
                    <input
                      type="text"
                      placeholder="Type keyword and press Enter..."
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const input = e.target as HTMLInputElement;
                          const keyword = input.value.trim();
                          if (keyword) {
                            const currentKeywords = content.keywords || [];
                            onChange('keywords', [...currentKeywords, keyword]);
                            input.value = '';
                          }
                        }
                      }}
                    />
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Use [exact match], "phrase match", or broad match.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Negative Keywords
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(content.negativeKeywords || []).map((keyword, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800"
                      >
                        {keyword}
                        {isEditing && (
                          <button
                            onClick={() => {
                              const newKeywords = content.negativeKeywords?.filter((_, i) => i !== index);
                              onChange('negativeKeywords', newKeywords);
                            }}
                            className="ml-2 text-red-600 hover:text-red-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                  {isEditing && (
                    <input
                      type="text"
                      placeholder="Type negative keyword and press Enter..."
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const input = e.target as HTMLInputElement;
                          const keyword = input.value.trim();
                          if (keyword) {
                            const currentKeywords = content.negativeKeywords || [];
                            onChange('negativeKeywords', [...currentKeywords, keyword]);
                            input.value = '';
                          }
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            )}

            {activeAssetSubTab === 'images' && (
              <div>
                <MediaUploader
                  creatives={content.creatives?.filter(c => c.type === 'image') || []}
                  onCreativesChange={(creatives) => {
                    const otherCreatives = content.creatives?.filter(c => c.type !== 'image') || [];
                    onChange('creatives', [...otherCreatives, ...creatives]);
                  }}
                  disabled={!isEditing}
                  platform="Google Ads"
                  acceptedTypes={['image/*']}
                  maxFiles={10}
                  blobManager={blobManager}
                  contentId={contentId}
                  apiBaseUrl={apiBaseUrl}
                />
              </div>
            )}

            {activeAssetSubTab === 'videos' && (
              <div>
                <MediaUploader
                  creatives={content.creatives?.filter(c => c.type === 'video') || []}
                  onCreativesChange={(creatives) => {
                    const otherCreatives = content.creatives?.filter(c => c.type !== 'video') || [];
                    onChange('creatives', [...otherCreatives, ...creatives]);
                  }}
                  disabled={!isEditing}
                  platform="Google Ads - Video"
                  acceptedTypes={['video/*']}
                  maxFiles={5}
                  blobManager={blobManager}
                  contentId={contentId}
                  apiBaseUrl={apiBaseUrl}
                />
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'targeting' && (
        <>
          {/* Targeting */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
          Targeting
        </h3>

        {/* Targeting Options */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Locations
            </label>
            <input
              type="text"
              value={content.locations || ''}
              onChange={(e) => onChange('locations', e.target.value)}
              disabled={!isEditing}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="United States, Canada..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Languages
            </label>
            <input
              type="text"
              value={content.languages || ''}
              onChange={(e) => onChange('languages', e.target.value)}
              disabled={!isEditing}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="English, Spanish..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Demographics
            </label>
            <input
              type="text"
              value={content.demographics || ''}
              onChange={(e) => onChange('demographics', e.target.value)}
              disabled={!isEditing}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="Age: 25-54, Gender: All..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Audience Segments
            </label>
            <textarea
              value={content.audiences || ''}
              onChange={(e) => onChange('audiences', e.target.value)}
              disabled={!isEditing}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 h-20"
              placeholder="In-market audiences, affinity audiences..."
            />
          </div>
        </div>
      </div>
        </>
      )}

      {activeTab === 'budget' && (
        <>
          {/* Budget & Bidding - Move content from Targeting section here */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
              Budget & Bidding
            </h3>

            {/* Total Monthly Spend Display */}
            {content.budget && content.budgetType && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Estimated Monthly Spend:</span>
                  <span className="text-2xl font-bold text-blue-600">
                    ${
                      content.budgetType === 'daily'
                        ? (content.budget * 30.4).toFixed(2)
                        : content.budgetType === 'monthly'
                        ? content.budget.toFixed(2)
                        : (content.budget / 12).toFixed(2)
                    }
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Based on {content.budgetType === 'daily' ? '30.4 days average' : content.budgetType} budget
                </p>
              </div>
            )}

            {/* Budget Settings */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget Amount
                </label>
                <input
                  type="number"
                  value={content.budget || ''}
                  onChange={(e) => onChange('budget', parseFloat(e.target.value))}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  placeholder="50.00"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget Type
                </label>
                <select
                  value={content.budgetType || 'daily'}
                  onChange={(e) => onChange('budgetType', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="daily">Daily</option>
                  <option value="total">Campaign Total</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bid Strategy
                </label>
                <select
                  value={content.bidStrategy || 'Maximize Clicks'}
                  onChange={(e) => onChange('bidStrategy', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="Manual CPC">Manual CPC</option>
                  <option value="Enhanced CPC">Enhanced CPC</option>
                  <option value="Maximize Clicks">Maximize Clicks</option>
                  <option value="Target CPA">Target CPA</option>
                  <option value="Target ROAS">Target ROAS</option>
                  <option value="Maximize Conversions">Maximize Conversions</option>
                </select>
              </div>
            </div>

            {/* Schedule */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={content.startDate || ''}
                  onChange={(e) => onChange('startDate', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={content.endDate || ''}
                  onChange={(e) => onChange('endDate', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
}